/**
 * Historial de deshacer y rehacer con parches de Immer (doc. 02 §5). Límite: 200 pasos por
 * sesión. La simulación y la reproducción no entran al historial: no son ediciones.
 */

import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import type { Proyecto } from '../nucleo/dominio/espacio';
import type { Comando } from './comandos';

enablePatches();

export const LIMITE_HISTORIAL = 200;

export interface EntradaHistorial {
  nombre: string;
  parches: Patch[];
  inversos: Patch[];
  /** Comandos seguidos con la misma clave se funden en un solo paso (empujones con flechas). */
  fundir?: string;
}

export interface Historial {
  pasado: EntradaHistorial[];
  futuro: EntradaHistorial[];
}

export const historialVacio = (): Historial => ({ pasado: [], futuro: [] });

export function aplicarComando(
  proyecto: Proyecto,
  comando: Comando,
  historial: Historial,
): { proyecto: Proyecto; historial: Historial; cambio: boolean } {
  const [siguiente, parches, inversos] = produceWithPatches(proyecto, comando.receta);
  if (parches.length === 0) return { proyecto, historial, cambio: false };
  const ultima = historial.pasado[historial.pasado.length - 1];
  if (comando.fundir && ultima?.fundir === comando.fundir && historial.futuro.length === 0) {
    // Mismo empujón seguido: un solo paso de deshacer con los cambios de los dos.
    const fundida: EntradaHistorial = {
      nombre: comando.nombre,
      parches: [...ultima.parches, ...parches],
      inversos: [...inversos, ...ultima.inversos],
      fundir: comando.fundir,
    };
    return {
      proyecto: siguiente,
      historial: { pasado: [...historial.pasado.slice(0, -1), fundida], futuro: [] },
      cambio: true,
    };
  }
  const entrada: EntradaHistorial = { nombre: comando.nombre, parches, inversos };
  if (comando.fundir) entrada.fundir = comando.fundir;
  const pasado = [...historial.pasado, entrada];
  if (pasado.length > LIMITE_HISTORIAL) pasado.shift();
  return { proyecto: siguiente, historial: { pasado, futuro: [] }, cambio: true };
}

export function deshacer(
  proyecto: Proyecto,
  historial: Historial,
): { proyecto: Proyecto; historial: Historial } | null {
  const ultima = historial.pasado[historial.pasado.length - 1];
  if (!ultima) return null;
  return {
    proyecto: applyPatches(proyecto, ultima.inversos),
    historial: { pasado: historial.pasado.slice(0, -1), futuro: [ultima, ...historial.futuro] },
  };
}

export function rehacer(
  proyecto: Proyecto,
  historial: Historial,
): { proyecto: Proyecto; historial: Historial } | null {
  const siguiente = historial.futuro[0];
  if (!siguiente) return null;
  return {
    proyecto: applyPatches(proyecto, siguiente.parches),
    historial: { pasado: [...historial.pasado, siguiente], futuro: historial.futuro.slice(1) },
  };
}
